import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Modal,
} from "@mui/material";
import TableFilterHeader from "../../components/TableHeader/TableFilterHeader";
import BaseCustomerTable from "../../components/BaseCustomerTable/BaseCustomerTable";
import PayrollAdminModal from "../../components/PayrollAdminModal/PayrollAdminModal";
import UserFace from "../../images/user.png";
import EditIcon from "../../images/editpencil.svg"; // ← add
import { commonRunQuery } from "../../util/API/loans";
import { getUserDetails } from "../../util/API/customers"; // ← add
import { decodeHtmlEntities } from "../../util/utilfunctions/utilFunctions";
import { ListPayrollEmployees } from "../../util/API/batch";
import Eye from "../../images/eye.svg";

/* ─── status badge colours ─────────────────────────────────────── */
const statusLabel = (status, userStatus = []) => {
  const found = userStatus.find(
    (s) => s?.userstatus?.statusID === String(status),
  );
  return found?.userstatus?.name ?? (status === "1" ? "Active" : "Inactive");
};

export default function ViewAssociates({
  parentAdmin,
  systemRoles,
  userStatus,
  onGoBack,
  updateUser, // ← needed by PayrollAdminModal
  setUpdateUser, // ← needed by PayrollAdminModal
  payrollUsers, // ← needed by PayrollAdminModal
}) {
  const [associates, setAssociates] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(5);
  const [lastVisitedRow, setLastVisited] = useState(null);

  /* ── Modify Roles modal state (mirrors PayrollAdminTable) ── */
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [employeesOpen, setEmployeesOpen] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [payrollEmployees, setPayrollEmployees] = useState([]);
  const [selectedAssociate, setSelectedAssociate] = useState(null);

  const parentId = parentAdmin?.User?.id;
  const parentCompany = parentAdmin?.User?.otherNames || "—";
  const parentName =
    `${parentAdmin?.User?.firstName ?? ""} ${parentAdmin?.User?.lastName ?? ""}`.trim();
  const parentEmail = parentAdmin?.User?.other1 ?? "";

  const cleanName = (name) =>
    decodeHtmlEntities(decodeHtmlEntities(name || ""))
      .replace(/[^a-zA-Z0-9\s'&.-]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  // /* ── fetch associates ─────────────────────────────────────────── */
  // const fetchAssociates = async () => {
  //   setIsLoading(true);
  //   try {
  //     const res = await commonRunQuery(381, parentId);
  //     const raw = res?.data?.body?.record;
  //     const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  //     setAssociates(list);
  //     setFiltered(list);
  //   } catch (err) {
  //     console.error("fetchAssociates error:", err);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  const fetchAssociates = async () => {
  setIsLoading(true);
  try {
    const res = await commonRunQuery(381, parentId);
    const raw = res?.data?.body?.record;

    // Cake5 returns record:"" on empty; Cake2 also does — guard both
    let list = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (raw && typeof raw === "object") {
      list = [raw];
    }
    // raw === "" or raw === null/undefined → list stays []

    setAssociates(list);
    setFiltered(list);
  } catch (err) {
    console.error("fetchAssociates error:", err);
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    if (parentId) fetchAssociates();
  }, [parentId]);

  /* ── search ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!searchText.trim()) {
      setFiltered(associates);
      return;
    }
    const q = searchText.toLowerCase();
    setFiltered(
      associates.filter(
        (r) =>
          r?.u?.firstName?.toLowerCase().includes(q) ||
          r?.u?.lastName?.toLowerCase().includes(q) ||
          (r?.u?.emailAccount || "").toLowerCase().includes(q) ||
          r?.u?.companyName?.toLowerCase().includes(q),
      ),
    );
    setPage(0);
  }, [searchText, associates]);

  /* ── Modify Roles handler (same pattern as PayrollAdminTable) ─── */
  const handleModifyRoles = async (associateUserId) => {
    const response = await getUserDetails(associateUserId);
    setSelectedUser(response);
    setModalOpen(true);
  };

  const getEmployeeValue = (employee, keys) => {
    const source =
      employee?.User ||
      employee?.Payrollemployee ||
      employee?.Employee ||
      employee?.Customer ||
      employee || {};
    const key = Object.keys(source).find((candidate) =>
      keys.includes(String(candidate).toLowerCase()),
    );
    return key ? source[key] ?? "" : "";
  };

  const handleViewEmployees = async (associate) => {
    const associateId = associate?.u?.id;
    if (!associateId) return;

    setSelectedAssociate(associate);
    setPayrollEmployees([]);
    setEmployeesOpen(true);
    setEmployeesLoading(true);

    try {
      const response = await ListPayrollEmployees(associateId);
      if (String(response?.callStatus) !== "1") {
        throw new Error(response?.payload?.msg || "Could not load employees.");
      }

      const payload = response?.payload?.record ?? response?.payload ?? [];
      setPayrollEmployees(
        Array.isArray(payload)
          ? payload
          : payload && typeof payload === "object"
            ? [payload]
            : [],
      );
    } catch (error) {
      console.error("ListPayrollEmployees failed:", error);
      setPayrollEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const downloadEmployeesCsv = () => {
    const headers = ["Account Number", "First Name", "Last Name", "Date of Birth"];
    const rows = payrollEmployees.map((employee) => [
      getEmployeeValue(employee, ["accountno", "account_number", "accountnumber"]),
      getEmployeeValue(employee, ["firstname", "first_name"]),
      getEmployeeValue(employee, ["lastname", "last_name"]),
      getEmployeeValue(employee, ["birthdate", "dateofbirth", "date_of_birth", "dob"]),
    ]);
    const csv = [
      headers.join(","),
      ...rows.map((row) => row.map((value) => JSON.stringify(value ?? "")).join(",")),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `payroll-employees-${selectedAssociate?.u?.id || "list"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* ── pagination ───────────────────────────────────────────────── */
  const pageCount = Math.ceil(filtered.length / rowsPerPage);
  const pageNumbers = Array.from({ length: pageCount }, (_, i) => i);
  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /* ── render ───────────────────────────────────────────────────── */
  return (
    <Box>
      {/* ── Modify Roles Modal (same props as PayrollAdminTable) ── */}
      <PayrollAdminModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        selectedUser={selectedUser}
        payrollFilterList={payrollUsers} // associates share the same user pool
        updateUser={updateUser}
        setUpdateUser={setUpdateUser}
        userStatus={userStatus}
        userRoles={systemRoles}
        payrollUsers={payrollUsers}
        handleSelectedUser={handleModifyRoles}
      />

      {/* ── Go Back ── */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          onClick={onGoBack}
          sx={{
            color: "#00365A",
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 500,
            borderRadius: "30px",
            border: "1px solid #EAEEF4",
            background: "#FFF",
            padding: "5px 20px",
          }}
        >
          Go Back
        </Button>
      </Box>

      {/* ── Parent admin banner ── */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "#15803d" }}
          >
            Associates under: {parentName}
          </Typography>
          <Typography variant="body2" sx={{ color: "#166534" }}>
            {parentEmail} &nbsp;·&nbsp; {cleanName(parentCompany)}
          </Typography>
        </Box>
        <Chip
          label={`${associates.length} associate${associates.length !== 1 ? "s" : ""}`}
          size="small"
          sx={{
            ml: "auto",
            backgroundColor: "#dcfce7",
            color: "#166534",
            fontWeight: 600,
          }}
        />
      </Box>

      {/* ── Search bar ── */}
      <TableFilterHeader
        handleSearch={(val) => setSearchText(val)}
        setOpen={() => {}}
        createAllowed={false}
        placeholder="Search Associates"
      />

      {/* ── Table ── */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography color="text.secondary">
            No associates found under this payroll admin.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: "none", mt: 1 }}>
          <Table sx={{ minWidth: 500 }} aria-label="associates table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>
                  <Typography className="customer-table-headers">
                    Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Company
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Email
                  </Typography>
                </TableCell>
                 <TableCell>
                  <Typography className="customer-table-headers">
                    User Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Phone
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Role
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Account Status
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography className="customer-table-headers">
                    Date Added
                  </Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography className="customer-table-headers">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.map((row) => (
                <TableRow
                  key={row?.u?.id}
                  onClick={() => setLastVisited(row?.u?.id)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      row?.u?.id === lastVisitedRow ? "#dbefff" : "inherit",
                    "&:hover": { backgroundColor: "#f0f7ff" },
                  }}
                >
                  {/* Avatar */}
                  <TableCell style={{ width: 50 }}>
                    <img
                      src={UserFace}
                      className="table-customer-photo"
                      alt="user"
                    />
                  </TableCell>

                  {/* Name */}
                  <TableCell style={{ width: 180 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.firstName} {row?.u?.lastName}
                    </Typography>
                  </TableCell>

                  {/* Company */}
                  <TableCell style={{ width: 180 }}>
                    <Typography
                      className="email-admin-text"
                      sx={{ fontWeight: 500 }}
                    >
                      {cleanName(row?.u?.companyName) || "—"}
                    </Typography>
                  </TableCell>

                  {/* Email */}
                  <TableCell style={{ width: 200 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.emailAccount || "—"}
                    </Typography>
                  </TableCell>
                    <TableCell style={{ width: 200 }}>
                    <Typography className="email-admin-text">
                     {row?.ue?.username?.endsWith("@4pay.me")
  ? row?.ue?.username?.split("@")[0]
  : row?.ue?.username || "—"}
                    </Typography>
                  </TableCell>

                  {/* Phone */}
                  <TableCell style={{ width: 150 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.phoneno || "—"}
                    </Typography>
                  </TableCell>

                  {/* Role badge */}
                  <TableCell style={{ width: 160 }}>
                    <Chip
                      label={row?.sr?.roleName || "Associate Payroll Admin"}
                      size="small"
                      sx={{
                        backgroundColor: "#eff6ff",
                        color: "#1d4ed8",
                        fontWeight: 500,
                        fontSize: "11px",
                      }}
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell style={{ width: 130 }}>
                    <span
                      className={
                        String(row?.u?.status) === "1"
                          ? "active-user"
                          : "disabled-user"
                      }
                    >
                      {statusLabel(row?.u?.status, userStatus)}
                    </span>
                  </TableCell>

                  {/* Date added */}
                  <TableCell style={{ width: 160 }}>
                    <Typography className="email-admin-text">
                      {row?.u?.dateAdded
                        ? new Date(row.u.dateAdded).toLocaleDateString()
                        : "—"}
                    </Typography>
                  </TableCell>

                  {/* Actions — stop propagation so row highlight doesn't fire */}
                  <TableCell
                    style={{ width: 160 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      className="modify-role-button"
                      onClick={() => handleModifyRoles(row?.u?.id)}
                    >
                      <img src={EditIcon} alt="Edit" />
                      Edit User
                    </Box>
                    <Box
                      className="modify-role-button"
                      onClick={() => handleViewEmployees(row)}
                    >
                      <img src={Eye} alt="View Employees" />
                      View Employees
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <BaseCustomerTable
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            pageNumbers={pageNumbers}
          />
        </TableContainer>
      )}

      <Modal open={employeesOpen} onClose={() => setEmployeesOpen(false)}>
        <Box className="payroll-employees-modal">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">
              Employees — {selectedAssociate?.u?.firstName || "Payroll Associate"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={downloadEmployeesCsv}
                disabled={employeesLoading || payrollEmployees.length === 0}
              >
                Download CSV
              </Button>
              <Button variant="contained" color="inherit" onClick={() => setEmployeesOpen(false)}>
                Close
              </Button>
            </Box>
          </Box>

          <Box className="payroll-employees-modal__summary">
            <span>Employees linked to this payroll group</span>
            <strong>
              {employeesLoading
                ? "Loading..."
                : `${payrollEmployees.length} employee${payrollEmployees.length === 1 ? "" : "s"}`}
            </strong>
          </Box>

          {employeesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : payrollEmployees.length === 0 ? (
            <Typography>No employees found for this Payroll Associate.</Typography>
          ) : (
            <TableContainer component={Paper} className="payroll-employees-modal__table">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Account Number</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Date of Birth</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payrollEmployees.map((employee, index) => (
                    <TableRow key={getEmployeeValue(employee, ["customerid", "id", "userid"]) || index}>
                      <TableCell>{getEmployeeValue(employee, ["accountno", "account_number", "accountnumber"]) || "—"}</TableCell>
                      <TableCell>{getEmployeeValue(employee, ["firstname", "first_name"]) || "—"}</TableCell>
                      <TableCell>{getEmployeeValue(employee, ["lastname", "last_name"]) || "—"}</TableCell>
                      <TableCell>{getEmployeeValue(employee, ["birthdate", "dateofbirth", "date_of_birth", "dob"]) || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Modal>
    </Box>
  );
  
}
